import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useOnboarding } from "@/context/OnboardingContext";
import { useLang } from "@/context/LanguageContext";
import LanguagePill from "@/components/LanguagePill";
import OnboardingExit from "@/components/OnboardingExit";
import { toast } from "sonner";

export default function OnboardingBirth() {
  const navigate = useNavigate();
  const { data, update, reset } = useOnboarding();
  const { t } = useLang();
  const [dob, setDob] = useState(data.birth?.date_of_birth || "");
  const [tob, setTob] = useState(data.birth?.time_of_birth || "");
  const [timeUnknown, setTimeUnknown] = useState(data.birth?.time_unknown || false);
  const [place, setPlace] = useState(data.birth?.place_of_birth || "");
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = dob && (tob || timeUnknown) && place && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      const birth = {
        date_of_birth: dob,
        time_of_birth: timeUnknown ? "12:00" : tob,
        time_unknown: timeUnknown,
        place_of_birth: place,
        latitude: 0, longitude: 0, timezone_offset: "+05:30",
      };
      update({ birth });
      await api.post("/onboarding/submit", {
        profile: data.profile,
        priorities: data.priorities,
        deep_dives: data.deep_dives,
        birth,
      });
      navigate("/reveal/loading");
    } catch (e) {
      toast.error(t("ob.birth.cant_save"));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen px-6 py-10 max-w-md mx-auto" style={{ backgroundColor: "var(--vedari-cream)", color: "var(--vedari-ink)" }}>
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-2 flex-1">
          {[1,2,3,4,5].map(i => <span key={i} className="h-[2px] flex-1 rounded-full" style={{ backgroundColor: i <= 5 ? "var(--vedari-gold)" : "var(--vedari-divider)" }} />)}
        </div>
        <LanguagePill persist />
        <OnboardingExit />
      </div>

      <h1 className="font-display text-4xl mb-2 leading-tight" data-testid="birth-title">{t("ob.birth.title")}</h1>
      <p className="text-body" style={{ color: "var(--vedari-ink-soft)" }}>
        {t("ob.birth.sub")}
      </p>

      <div className="space-y-5 mt-8">
        <div>
          <label className="block text-body-sm font-medium mb-2" style={{ color: "var(--vedari-ink-soft)" }}>{t("ob.birth.dob")}</label>
          <input type="date" className="vedari-input" value={dob} onChange={e => setDob(e.target.value)} max="2008-12-31" data-testid="birth-date" />
        </div>
        <div>
          <label className="block text-body-sm font-medium mb-2" style={{ color: "var(--vedari-ink-soft)" }}>{t("ob.birth.tob")}</label>
          <input type="time" className="vedari-input" value={tob} onChange={e => setTob(e.target.value)} disabled={timeUnknown} data-testid="birth-time" />
          <label className="flex items-center gap-3 mt-3 text-body-sm">
            <input type="checkbox" checked={timeUnknown} onChange={e => setTimeUnknown(e.target.checked)} className="w-4 h-4 accent-[var(--vedari-gold)]" data-testid="birth-time-unknown" />
            <span style={{ color: "var(--vedari-ink-soft)" }}>{t("ob.birth.time_unknown")}</span>
          </label>
          {timeUnknown && (
            <p className="text-caption mt-3 p-3 rounded-md" style={{ color: "var(--vedari-amber)", backgroundColor: "rgba(196,144,80,0.08)" }}>
              {t("ob.birth.time_warning")}
            </p>
          )}
        </div>
        <div>
          <label className="block text-body-sm font-medium mb-2" style={{ color: "var(--vedari-ink-soft)" }}>{t("ob.birth.place")}</label>
          <input className="vedari-input" value={place} onChange={e => setPlace(e.target.value)} placeholder={t("ob.birth.place_ph")} data-testid="birth-place" />
        </div>
      </div>

      <button onClick={submit} disabled={!canSubmit} className="vedari-btn-primary w-full mt-10" data-testid="reveal-map-btn">
        {submitting ? t("ob.birth.preparing") : t("ob.birth.reveal")}
      </button>
      <button onClick={() => navigate("/onboarding/life-letter")} className="vedari-btn-tertiary w-full mt-2" data-testid="birth-back-btn">
        {t("common.back")}
      </button>
    </div>
  );
}
